// src/pages/Dashboard.jsx
import { useMemo, useState } from 'react';
import { parseISO, isValid, addWeeks } from 'date-fns';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useUser } from '../contexts/UserContext';
import { useOrder } from '../contexts/OrderContext';
import { useAppointment } from '../contexts/AppointmentContext';
import { formatDate, calculateAge } from '../utilities/utilities';
import { useWeightCalculations } from '../hooks/useWeightCalculations';

import OrdersPreview from '../components/OrdersPreview';
import AppointmentsPreview from '../components/AppointmentsPreview';
import Spinner from '../components/Spinner';
import GenericConfirmationModal from '../components/GenericConfirmationModal';
import { getAppointmentActionState, isUpcomingAppointment, refundFailureMessage } from '../utilities/appointmentRules';
import { getFirstUnpaidOrder, getRefillActionState } from '../utilities/refillRules';
import { useNavigateWithContext } from '../hooks/useNavigateWithContext';
import { useRefillButtonGate } from '../hooks/useRefillButtonGate';
import { useMinuteTicker } from '../hooks/useMinuteTicker';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

export default function Dashboard() {
  const navigate = useNavigate();
  const navigateWithContext = useNavigateWithContext();
  const { requestRefill, gateModal: refillGateModal } = useRefillButtonGate();
  const userContext = useUser();
  const orderContext = useOrder();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelingAppointment, setIsCancelingAppointment] = useState(false);
  const appointmentClock = useMinuteTicker();

  // — pull from contexts —
  const { user, error: userError } = useUser();
  const {
    orders,
    orderHistory,
    isLoading: isLoadingOrders,
    error: ordersError,
  } = useOrder();
  const {
    appointments,
    isLoading: isLoadingAppointments,
    error: appointmentsError,
    cancelAppointment,
  } = useAppointment();

  const historyData = orderHistory || [];

  // Current weight must come from order_history when available; orders contain product info, not measurements.
  const currentWeight = orderHistory?.[0]?.weight != null
    ? parseFloat(orderHistory[0].weight)
    : parseFloat(userContext?.user?.weight);

  const goalWeight = userContext?.user?.goal_weight != null
    ? parseFloat(userContext?.user.goal_weight)
    : null;

  const nextAppointment = useMemo(() => {
    if (!Array.isArray(appointments)) return null;
    return appointments
      .filter(appt => appt?.date_appt && isValid(appt.date_appt) && isUpcomingAppointment(appt))
      .sort((a, b) => new Date(a.date_appt) - new Date(b.date_appt))[0] || null;
  }, [appointments, appointmentClock]);

  const nextAppointmentAction = useMemo(
    () => nextAppointment ? getAppointmentActionState(nextAppointment) : null,
    [nextAppointment, appointmentClock]
  );
  const firstUnpaidOrder = useMemo(() => getFirstUnpaidOrder(orders), [orders]);
  const latestRefillState = useMemo(
    () => getRefillActionState(orderContext?.latestOrder, orders),
    [orderContext?.latestOrder, orders]
  );
  const missingProfileFields = useMemo(() => {
    const currentUser = userContext?.user;
    if (!currentUser) return [];

    const fields = [];
    if (!currentUser.height) fields.push('height');
    if (!(Number(currentWeight) > 0)) fields.push('current weight');
    if (!(Number(goalWeight) > 0)) fields.push('goal weight');
    if (!currentUser.phone_mobile && !currentUser.phone_home && !currentUser.phone_work && !currentUser.phone_work_home) {
      fields.push('mobile phone');
    }

    return fields;
  }, [currentWeight, goalWeight, userContext?.user]);
  const hasOrders = Array.isArray(orders) && orders.length > 0;
  const hasAppointments = Array.isArray(appointments)
    ? appointments.filter(a => a.status?.toLowerCase() !== 'canceled').length > 0
    : false;
  const genderValue = String(userContext?.user?.gender || '').trim().toLowerCase();
  const genderLabel = genderValue === 'm' || genderValue === 'male'
    ? 'Male'
    : genderValue === 'f' || genderValue === 'female'
      ? 'Female'
      : 'N/A';

  const nextAction = useMemo(() => {
    if (firstUnpaidOrder) {
      return {
        type: 'payment',
        title: 'Complete Payment',
        body: `Order #${firstUnpaidOrder.order_id} is waiting for payment before it can move forward.`,
        primaryLabel: 'Pay Now',
        secondaryLabel: 'View Orders',
      };
    }

    if (nextAppointmentAction?.isJoinable && nextAppointment) {
      return {
        type: 'join',
        title: 'Join Your Appointment',
        body: `Your appointment is open now. Status: ${nextAppointment.status}.`,
        primaryLabel: 'Join Now',
        secondaryLabel: 'View Details',
      };
    }

    if (missingProfileFields.length > 0) {
      return {
        type: 'profile',
        title: 'Complete Your Profile',
        body: `Add ${missingProfileFields.join(', ')} so appointments, refills, and payment forms have the details they need.`,
        primaryLabel: 'Update Profile',
        secondaryLabel: null,
      };
    }

    if (latestRefillState?.isRefillable && orderContext?.latestOrder) {
      return {
        type: 'refill',
        title: 'Request Your Refill',
        body: `${orderContext.latestOrder.product_name || 'Your medication'} ${orderContext.latestOrder.dosage || ''} is eligible for a refill request.`,
        primaryLabel: 'Request Refill',
        secondaryLabel: 'View Orders',
      };
    }

    if (latestRefillState?.isUnderReview && orderContext?.latestOrder) {
      return {
        type: 'review',
        title: 'Provider Review in Progress',
        body: `Order #${orderContext.latestOrder.order_id} is under provider review. Payment and refill actions will appear when review is complete.`,
        primaryLabel: 'View Orders',
        secondaryLabel: null,
      };
    }

    if (nextAppointment) {
      const minutes = nextAppointmentAction?.minutesUntilAppt;
      const timing = Number.isFinite(minutes) && minutes > 15
        ? 'Join opens 15 minutes before the appointment.'
        : 'Your appointment is coming up.';
      return {
        type: 'appointment',
        title: 'Next Appointment',
        body: `${nextAppointment.date_appt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${nextAppointment.date_appt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}. ${timing}`,
        primaryLabel: 'View Details',
        secondaryLabel: nextAppointmentAction?.isCancellable ? 'Cancel' : null,
      };
    }

    return {
      type: 'schedule',
      title: hasAppointments ? 'Schedule a Follow-up Consultation' : 'Schedule Your First Consultation',
      body: hasAppointments
        ? 'Book your next appointment when you need provider guidance or a plan review.'
        : 'Book your first appointment so a provider can review your plan and next steps.',
      primaryLabel: 'Schedule Appointment',
      secondaryLabel: null,
    };
  }, [firstUnpaidOrder, hasAppointments, latestRefillState, missingProfileFields, nextAppointment, nextAppointmentAction, orderContext?.latestOrder]);

  const handleNextActionPrimary = () => {
    if (nextAction.type === 'payment') {
      navigateWithContext(firstUnpaidOrder.order_id, '/payment', 'order');
      return;
    }
    if (nextAction.type === 'join') {
      navigateWithContext(nextAppointment.appointment_id, '/encounter', 'appointment');
      return;
    }
    if (nextAction.type === 'refill') {
      requestRefill(orderContext.latestOrder.order_id);
      return;
    }
    if (nextAction.type === 'profile') {
      navigate('/profile');
      return;
    }
    if (nextAction.type === 'review') {
      navigate('/orders');
      return;
    }
    if (nextAction.type === 'appointment') {
      navigate('/appointments');
      return;
    }
    navigate('/schedule');
  };

  const handleNextActionSecondary = () => {
    if (nextAction.type === 'appointment' && nextAppointmentAction?.isCancellable) {
      setShowCancelConfirm(true);
      return;
    }
    if (nextAction.type === 'payment' || nextAction.type === 'refill') {
      navigate('/orders');
      return;
    }
    navigate('/appointments');
  };

  const handleCancelNextAppointment = async () => {
    if (!nextAppointment?.appointment_id) return;

    setIsCancelingAppointment(true);
    const result = await cancelAppointment(nextAppointment.appointment_id);
    setIsCancelingAppointment(false);

    if (result?.error) {
      toast.error(result.error);
      return;
    }

    setShowCancelConfirm(false);
    toast.success('Appointment canceled.');
    const refundMsg = refundFailureMessage(result);
    if (refundMsg) toast.error(refundMsg, { duration: 10000 });
  };

  // — derived flags —
  const showWelcome = !isLoadingOrders && !isLoadingAppointments && !hasOrders && !hasAppointments;

  // — weight/history inputs —
  const rawStartDate   = userContext?.user?.start_date;
  // if userContext?.user.start_date is missing/invalid, pick the oldest history entry:
  const fallbackStart  = historyData.length
    ? historyData[historyData.length - 1].reported_date
    : null;
  const startDateValue = (rawStartDate && isValid(parseISO(rawStartDate)))
    ? rawStartDate
    : (fallbackStart instanceof Date ? fallbackStart : null);

  // — hook gives us BMI, avg loss, total loss, projection —
  const {
    bmi,
    averageLossPerWeek,
    totalWeightLost,
    projectedGoalDateFromStart
  } = useWeightCalculations({
    height:      userContext?.user?.height,
    weight:      currentWeight,
    goalWeight:  goalWeight,
    startDate:   startDateValue,
    historyData,
    // useTodayAsStart: false  ← default
  });

  // instead of your old isDataReady, do something like this:
  const isChartReady = useMemo(() => {
    // make sure the user context has finished initializing
    if (userContext.loadingSession) return false;

    // make sure your orderHistory has arrived
    if (orderContext.isLoading) return false;

    // must have at least two history points to calculate a trend
    if (!Array.isArray(orderContext.orderHistory) ||
        orderContext.orderHistory.length < 2
    ) return false;

    // must have user height & goal
    const u = userContext.user;
    if (!u || !u.height || !u.goal_weight) return false;

    return true;
  }, [
    userContext.loadingSession,
    orderContext.isLoading,
    orderContext.orderHistory,
    userContext.user?.height,
    userContext.user?.goal_weight,
  ]);

  // --- Chart Data Processing ---
  const chartData = useMemo(() => {
    if (!isChartReady) {
      return { labels: [], datasets: [] };
    }

    // 1. Base historical points from order_history table
    const historicalPoints = (orderContext?.orderHistory || [])
      .filter(item => item.reported_date && item.weight != null && !isNaN(+item.weight))
      .map(item => ({
        x: item.reported_date.valueOf(),
        y: parseFloat(item.weight),
      }));

    // 2. Seed in the very first "start" point
    // BUG FIX (2026-01-26): The filter used i.reported_date_obj which doesn't exist.
    // orderHistory items have 'reported_date' (Date object), not 'reported_date_obj'.
    // This caused firstRelevantWeight to always be undefined, falling back to user.weight.
    let allPoints = [...historicalPoints];
    if (userContext?.user?.start_date && userContext?.user?.weight != null) {
      const startDate = parseISO(userContext?.user.start_date);
      if (isValid(startDate)) {
        // FIXED: Use reported_date instead of reported_date_obj
        const firstRelevantWeight = orderContext?.orderHistory
          .filter(i => i.reported_date && i.weight != null && i.reported_date >= startDate)
          .slice(-1)[0]?.weight;

        const startWeightForChart = (firstRelevantWeight != null && !isNaN(+firstRelevantWeight))
          ? parseFloat(firstRelevantWeight)
          : parseFloat(userContext?.user.weight);

        allPoints.push({ x: startDate.valueOf(), y: startWeightForChart });
      }
    }

    // 3. Seed in the projected “goal” point
    let goalDateTimestamp;
    const today = Date.now();
    if (projectedGoalDateFromStart) {
      const proj = parseISO(projectedGoalDateFromStart);
      if (isValid(proj) && proj.valueOf() > today) {
        goalDateTimestamp = proj.valueOf();
      }
    }
    if (!goalDateTimestamp && userContext?.user?.goal_date && userContext?.user.goal_date !== '0000-00-00') {
      const gd = parseISO(userContext?.user.goal_date);
      if (isValid(gd) && gd.valueOf() > today) {
        goalDateTimestamp = gd.valueOf();
      }
    }
    if (goalDateTimestamp && userContext?.user?.goal_weight != null) {
      allPoints.push({ x: goalDateTimestamp, y: parseFloat(userContext?.user.goal_weight) });
    }

    // 4. De-dupe & sort
    const uniqueSortedPoints = Array.from(
      new Map(allPoints.map(p => [p.x, p])).values()
    ).sort((a, b) => a.x - b.x);

    return {
      datasets: uniqueSortedPoints.length
        ? [{
            label: 'Weight (lbs)',
            data: uniqueSortedPoints,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.3)',
            tension: 0.1,
            pointRadius: 4,
            pointHoverRadius: 8,
          }]
        : []
    };
  }, [isChartReady, orderContext?.orderHistory, userContext?.user?.start_date, userContext?.user?.weight, userContext?.user?.goal_date, userContext?.user?.goal_weight, projectedGoalDateFromStart]);

  // --- Chart Options ---
  const chartOptions = useMemo(() => {
    if (!isChartReady) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true }, title: { display: false } },
        scales: {
          x: { type: 'time', time: { unit: 'week' }, title: { display: true, text: 'Date' } },
          y: { title: { display: true, text: 'Weight (lbs)' } },
        },
      };
    }

    let minDateTimestamp, maxDateTimestamp;
    let yMin, yMax;
    // x-axis: enforce start_date … goal_date/projectedGoalDate
    if (userContext?.user?.start_date) {
      const sd = parseISO(userContext?.user.start_date);
      if (isValid(sd)) minDateTimestamp = sd.valueOf();
    }
    const today = Date.now();
    if (projectedGoalDateFromStart) {
      const pd = parseISO(projectedGoalDateFromStart);
      if (isValid(pd) && pd.valueOf() > today) {
        maxDateTimestamp = pd.valueOf();
      }
    }
    if (!maxDateTimestamp && userContext?.user?.goal_date && userContext?.user.goal_date !== '0000-00-00') {
      const gd = parseISO(userContext?.user.goal_date);
      if (isValid(gd) && gd.valueOf() > today) {
        maxDateTimestamp = gd.valueOf();
      }
    }
    // BUG FIX (2026-01-26): The fallback of start_date + 8 weeks cuts off recent data when
    // projectedGoalDate is in the past (goal already reached) or goal_date is not set.
    // Fix: Use the latest data point date (or today) + 2 weeks buffer as the fallback max.
    if (!maxDateTimestamp) {
      const dataPoints = chartData.datasets[0]?.data || [];
      const latestDataTimestamp = dataPoints.length > 0
        ? Math.max(...dataPoints.map(p => p.x))
        : today;
      // Use whichever is later: latest data point or today, plus 2 weeks buffer
      maxDateTimestamp = addWeeks(new Date(Math.max(latestDataTimestamp, today)), 2).valueOf();
    }

    // y-axis: pad min/max by buffer
    const dataPoints = chartData.datasets[0]?.data || [];
    const dataYs = dataPoints.map(p => p.y);
    const dataMinY = dataYs.length ? Math.min(...dataYs) : (userContext?.user?.goal_weight ?? 0);
    const dataMaxY = dataYs.length ? Math.max(...dataYs) : (userContext?.user?.weight ?? 0);
    const yBuffer = 10;
    yMin = Math.max(0, Math.floor(Math.min(dataMinY, userContext?.user?.goal_weight ?? dataMinY) - yBuffer));
    yMax = Math.ceil(Math.max(dataMaxY, userContext?.user?.weight ?? dataMaxY) + yBuffer);
    if (yMax <= yMin) yMax = yMin + yBuffer * 2;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true },
        tooltip: { enabled: true },
        title: { display: false },
      },
      scales: {
        x: {
          type: 'time',
          time: { unit: 'week' },
          title: { display: true, text: 'Date' },
          min: minDateTimestamp,
          max: maxDateTimestamp,
        },
        y: {
          beginAtZero: false,
          title: { display: true, text: 'Weight (lbs)' },
          min: yMin,
          max: yMax,
        },
      },
    };
  }, [
    isChartReady,
    userContext?.user?.start_date,
    userContext?.user?.goal_date,
    userContext?.user?.weight,
    userContext?.user?.goal_weight,
    projectedGoalDateFromStart,
    chartData
  ]);

	  // — render —
  if (userContext?.loadingSession && !userContext?.user?.userid) {
    return <Spinner overlay size="lg" />;
  }

	  return (
	    <>
      {/* user header */}
      {user && (
        <div className="flex flex-col md:flex-row md:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-primary">
              {userContext?.user.prefix} {userContext?.user.first_name} {userContext?.user.last_name} {userContext?.user.suffix}
            </h2>
            <p className="text-neutral-dark text-sm">
              {formatDate(userContext?.user.birthdate) || 'N/A'} · {genderLabel} · {calculateAge(userContext?.user.birthdate)} yrs
            </p>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {bmi && <p><strong>BMI:</strong> {bmi}</p>}
              {averageLossPerWeek != null && <p><strong>Avg Loss:</strong> {averageLossPerWeek.toFixed(1)} lbs/wk</p>}
              {totalWeightLost    != null && <p><strong>Total Loss:</strong> {totalWeightLost.toFixed(1)} lbs</p>}
              {projectedGoalDateFromStart && goalWeight != null && (
                <p><strong>Proj Goal:</strong> {formatDate(projectedGoalDateFromStart)}</p>
              )}
            </div>
            {(userError || ordersError || appointmentsError) && (
              <p className="text-red-500 text-sm mt-1">
                {userError}{ordersError}{appointmentsError}
              </p>
            )}
          </div>
          <div className="mt-4 md:mt-0">
            <button
              className="bg-button text-white px-4 py-2 rounded"
              onClick={() => navigate('/schedule')}
            >
              Request Consultation
            </button>
          </div>
        </div>
	      )}

      {!showWelcome && nextAction && (
        <section className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-primary">{nextAction.title}</h3>
              <p className="text-sm text-neutral-dark">
                {nextAction.body}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleNextActionPrimary}
                className={`${nextAction.type === 'join' ? 'bg-green-600 hover:bg-green-700' : 'bg-button hover:bg-button-light'} px-4 py-2 text-white rounded`}
              >
                {nextAction.primaryLabel}
              </button>
              {nextAction.secondaryLabel && (
                <button
                  type="button"
                  onClick={handleNextActionSecondary}
                  className={`${nextAction.secondaryLabel === 'Cancel' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-secondary hover:bg-secondary-dark text-white'} px-4 py-2 rounded`}
                >
                  {nextAction.secondaryLabel}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {showCancelConfirm && (
        <GenericConfirmationModal
          isOpen={showCancelConfirm}
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelNextAppointment}
          title="Cancel Appointment"
          message="Are you sure you want to cancel this appointment?"
          textCancel="No"
          textConfirm={isCancelingAppointment ? 'Canceling...' : 'Yes'}
        />
      )}

	      {/* weight chart */}
      {!showWelcome && (
        <div className="bg-transparent p-4 rounded shadow-inner mb-4">
          <h3 className="text-lg font-semibold text-primary mb-2 text-center">Weight Progress</h3>
          <div className="relative h-64 md:h-80">
            {orderContext?.isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner>
                  <p className="text-neutral-dark">Loading weight data…</p>
                </Spinner>
              </div>
            ) : chartData.datasets[0]?.data.length ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <p className="text-center text-neutral-dark pt-10">
                No weight data to display.
              </p>
            )}
          </div>
        </div>
      )}

      {/* orders & appointments panels */}
      {!showWelcome ? (
        <div className="flex flex-col md:flex-row gap-4 mt-4">
          <div className="w-full md:w-1/2 p-4 bg-neutral-light rounded shadow-sm">
            <OrdersPreview />
          </div>
          <div className="w-full md:w-1/2 p-4 bg-neutral-light rounded shadow-sm">
            <AppointmentsPreview />
          </div>
        </div>
      ) : (
        <div className="bg-highlight-light p-6 rounded-lg shadow-xl mx-auto max-w-3xl my-6 text-center">
          <h3 className="text-2xl font-semibold text-primary mb-3">Welcome to Your Weight Loss Journey!</h3>
          <p className="text-neutral-dark mb-5">
            Schedule your first consultation so a provider can review your plan and prescribe treatment when appropriate.
          </p>
          <button
            className="bg-button text-white px-6 py-3 rounded"
            onClick={() => navigate('/schedule')}
          >
            Request Consultation
          </button>
        </div>
      )}
      {refillGateModal}
    </>
  );
}
